import { Box, pn, Skeleton, Stack } from "@compass/core";
import { Export, Legend, Series, Tooltip } from "devextreme-react/pie-chart";
import PieChart from "devextreme-react/pie-chart";
import Chart, { Label as ChartLabel, Legend as ChartLegend, Series as ChartSeries, Tooltip as ChartTooltip, ValueAxis } from "devextreme-react/chart";
import { EligibilityDistributionDto } from "generated/openApi";
import { useEligibilityData } from "../contexts/EligibilityDataContext";
import { DashboardCard } from "../components/DashboardCard";
import { useRef, useState } from "react";
import { EligibilityFilterControls } from "./EligibilityFilterControls";
import { DashboardChartViewSelect } from "../components/DashboardChartViewSelect";
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from "../types";

const EligibilityChartContent = (props: {
    chartRef: React.RefObject<PieChart>;
    cartesianChartRef: React.RefObject<Chart>;
    chartView: DashboardChartView;
    setChartView: (view: DashboardChartView) => void;
}) => {
    const { data, loading, error } = useEligibilityData();

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                gap={2}
                sx={{ mb: 2 }}
            >
                <EligibilityFilterControls /><DashboardChartViewSelect dashboard="eligibility" value={props.chartView} onChange={props.setChartView} />
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex", justifyContent: "center" }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={400} />
                ) : error ? (
                    <Box sx={{ p: 2, color: "error.main" }}>{error}</Box>
                ) : (
                    <Box sx={{ width: "100%", maxWidth: 600, aspectRatio: "4 / 3" }}>
                        {(props.chartView === 'pie' || props.chartView === 'fullPie') ? <PieChart
                            ref={props.chartRef}
                            id="eligibility-chart"
                            type={props.chartView === 'fullPie' ? 'pie' : 'doughnut'}
                            dataSource={data}
                            palette={DASHBOARD_DISTRIBUTION_COLORS}
                            height="100%"
                        >
                            <Series
                                argumentField={pn<EligibilityDistributionDto>("eligibilityName")}
                                valueField={pn<EligibilityDistributionDto>("studentCount")}
                            />
                            <Legend
                                visible={true}
                                verticalAlignment="bottom"
                                horizontalAlignment="center"
                                columnCount={2}
                            />
                            <Tooltip
                                enabled={true}
                                customizeTooltip={(arg) => ({
                                    text: `${arg.argumentText}\nStudents: ${arg.valueText} (${arg.percentText})`,
                                })}
                            />
                            <Export enabled={false} />
                        </PieChart> : <Chart ref={props.cartesianChartRef} dataSource={data} height="100%" rotated={props.chartView === 'horizontalBar'} palette={DASHBOARD_DISTRIBUTION_COLORS} customizePoint={(args: any) => ({ color: getDashboardDistributionColor(args.index) })}>
                            <ChartSeries type="bar" argumentField={pn<EligibilityDistributionDto>("eligibilityName")} valueField={pn<EligibilityDistributionDto>("studentCount")}><ChartLabel visible /></ChartSeries>
                            <ValueAxis allowDecimals={false} /><ChartLegend visible={false} /><ChartTooltip enabled />
                        </Chart>}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export const EligibilityChartCard = () => {
    const chartRef = useRef<PieChart>(null);
    const cartesianChartRef = useRef<Chart>(null);
    const [chartView, setChartView] = useState<DashboardChartView>(DASHBOARD_CHART_VIEW_CONFIG.eligibility.recommendedView);

    const handleExport = () => {
        if (chartView === 'pie' || chartView === 'fullPie') chartRef.current?.instance.exportTo("EligibilityCriteria", "png");
        else cartesianChartRef.current?.instance.exportTo("EligibilityCriteria", "png");
    };

    return (
        <DashboardCard title="Eligibility Criteria" onExport={handleExport}>
            <EligibilityChartContent chartRef={chartRef} cartesianChartRef={cartesianChartRef} chartView={chartView} setChartView={setChartView} />
        </DashboardCard>
    );
};
